import React, { useState, useEffect } from 'react';
import { FaTimes, FaChartBar, FaCalendarAlt, FaQrcode, FaCoins, FaStore, FaBuilding } from 'react-icons/fa';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { supabase } from '../../services/supabase';
import { useAuth } from '../../context/AuthContext';

// Ümumi statistika sorğuları (RLS-ə görə açıq cədvəllər) üçün
const db = supabase;


const STATUS_LABELS = {
  confirmed: { label: 'Təsdiqlənib', color: '#4CAF50' },
  pending:   { label: 'Gözlənilir',  color: '#FF9800' },
  cancelled: { label: 'Ləğv edilib', color: '#F44336' },
  completed: { label: 'Tamamlandı',  color: '#2196F3' },
};

function timeAgo(date) {
  const s = Math.floor((new Date() - new Date(date)) / 1000);
  if (s < 60)    return `${s} san əvvəl`;
  if (s < 3600)  return `${Math.floor(s / 60)} dəq əvvəl`;
  if (s < 86400) return `${Math.floor(s / 3600)} saat əvvəl`;
  return `${Math.floor(s / 86400)} gün əvvəl`;
}

function fmtDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleString('az-AZ', {
    day: '2-digit', month: '2-digit', year: 'numeric',
    hour: '2-digit', minute: '2-digit',
  });
}

function fmtDateOnly(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('az-AZ', {
    day: '2-digit', month: 'long', year: 'numeric',
  });
}

// ─── Tab: Ümumi ────────────────────────────────────────────────────────────────
function OverviewTab({ user, stats, topBusinesses }) {
  if (!stats) return <div style={s.center}>Yüklənir...</div>;
  const cards = [
    { icon: '📍', label: 'Ümumi Check-in',    value: stats.totalCheckins,      color: '#2196F3' },
    { icon: '⭐', label: 'Cari Xallar',        value: stats.points,             color: '#FFD700' },
    { icon: '🏢', label: 'Ziyarət Etdiyi Yerlər', value: stats.uniqueBusinesses, color: '#FF6B35' },
    { icon: '📅', label: 'Rezervasiyalar',     value: stats.reservationCount,   color: '#9C27B0' },
    { icon: '👥', label: 'Dostlar',            value: stats.friendCount,        color: '#4CAF50' },
    { icon: '🎯', label: 'Meetup-lar',         value: stats.meetupCount,        color: '#00BCD4' },
    { icon: '🎰', label: 'Spin Sayı',          value: stats.spinCount,          color: '#E91E63' },
    { icon: '📆', label: 'Hesab Yaşı (gün)',   value: stats.accountAge,         color: '#607D8B' },
  ];
  return (
    <div>
      <div style={s.statsGrid}>
        {cards.map(c => (
          <div key={c.label} style={s.statCard}>
            <div style={{ fontSize: 32 }}>{c.icon}</div>
            <div>
              <p style={s.statLabel}>{c.label}</p>
              <h3 style={{ ...s.statValue, color: c.color }}>{c.value ?? 0}</h3>
            </div>
          </div>
        ))}
      </div>

      {topBusinesses.length > 0 && (
        <div style={s.section}>
          <h3 style={s.sectionTitle}>🏆 Ən Çox Ziyarət Etdiyi Yerlər (TOP 5)</h3>
          <ResponsiveContainer width="100%" height={220}>
            <BarChart data={topBusinesses}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} stroke="#999" />
              <YAxis tick={{ fontSize: 11 }} stroke="#999" />
              <Tooltip formatter={v => [`${v} check-in`, 'Ziyarət']}
                contentStyle={{ borderRadius: 8, fontSize: 13 }} />
              <Bar dataKey="count" fill="#FF6B35" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      )}

      <div style={s.section}>
        <h3 style={s.sectionTitle}>📊 Performans</h3>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10 }}>
          {stats.totalCheckins >= 50 && <span style={s.badge('#4CAF50')}>🔥 Aktiv İstifadəçi</span>}
          {stats.uniqueBusinesses >= 10 && <span style={s.badge('#2196F3')}>🌍 Explorer</span>}
          {stats.friendCount >= 10 && <span style={s.badge('#9C27B0')}>👥 Social Butterfly</span>}
          {stats.points >= 1000 && <span style={s.badge('#FFD700', '#333')}>💎 Top Scorer</span>}
          {stats.reservationCount >= 5 && <span style={s.badge('#FF6B35')}>📅 Rezervasiya Ustası</span>}
          {(!stats.totalCheckins || stats.totalCheckins < 5) &&
            <span style={s.badge('#FF9800')}>⚠️ Yeni İstifadəçi</span>}
        </div>
      </div>
    </div>
  );
}

// ─── Tab: Rezervasiyalar ───────────────────────────────────────────────────────
function ReservationsTab({ userId }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: rows } = await db
          .from('reservations')
          .select(`
            id, reservation_date, reservation_time, party_size, status, notes, created_at,
            business_locations(id, name, address, city),
            businesses(id, name)
          `)
          .eq('user_id', userId)
          .order('reservation_date', { ascending: false })
          .limit(100);
        setData(rows || []);
      } catch (_) { setData([]); }
      finally { setLoading(false); }
    })();
  }, [userId]);

  if (loading) return <div style={s.center}>Yüklənir...</div>;
  if (!data.length) return <div style={s.empty}>📅 Hələ rezervasiya yoxdur</div>;

  return (
    <div>
      <p style={s.countNote}>{data.length} rezervasiya tapıldı</p>
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr style={s.thead}>
              <th style={s.th}>Kafe</th>
              <th style={s.th}>Tarix</th>
              <th style={s.th}>Saat</th>
              <th style={s.th}>Nəfər</th>
              <th style={s.th}>Status</th>
              <th style={s.th}>Qeyd</th>
              <th style={s.th}>Yaradılıb</th>
            </tr>
          </thead>
          <tbody>
            {data.map(r => {
              const loc = r.business_locations || {};
              const biz = r.businesses || {};
              const st  = STATUS_LABELS[r.status] || { label: r.status || '—', color: '#999' };
              return (
                <tr key={r.id} style={s.tr}>
                  <td style={s.td}>
                    <strong>{loc.name || biz.name || '—'}</strong>
                    {loc.city ? <div style={s.sub}>📍 {loc.city}</div> : null}
                  </td>
                  <td style={s.td}>{fmtDateOnly(r.reservation_date)}</td>
                  <td style={s.td}>{r.reservation_time || '—'}</td>
                  <td style={{ ...s.td, textAlign: 'center' }}>{r.party_size || '—'}</td>
                  <td style={s.td}>
                    <span style={s.pill(st.color)}>
                      {st.label}
                    </span>
                  </td>
                  <td style={{ ...s.td, color: '#888', fontSize: 12 }}>{r.notes || '—'}</td>
                  <td style={{ ...s.td, color: '#aaa', fontSize: 12 }}>{timeAgo(r.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Tab: QR Tarixçəsi ────────────────────────────────────────────────────────
function CheckinsTab({ userId }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: rows } = await db
          .from('check_ins')
          .select(`
            id, created_at, points_earned,
            business_locations(id, name, address, city),
            businesses(id, name, city)
          `)
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(100);
        setData(rows || []);
      } catch (_) { setData([]); }
      finally { setLoading(false); }
    })();
  }, [userId]);

  if (loading) return <div style={s.center}>Yüklənir...</div>;
  if (!data.length) return <div style={s.empty}>📍 Hələ QR check-in yoxdur</div>;

  return (
    <div>
      <p style={s.countNote}>{data.length} QR oxunması tapıldı</p>
      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr style={s.thead}>
              <th style={s.th}>Kafe / Restoran</th>
              <th style={s.th}>Şəhər</th>
              <th style={s.th}>Ünvan</th>
              <th style={s.th}>Xal Qazandı</th>
              <th style={s.th}>Tarix & Saat</th>
            </tr>
          </thead>
          <tbody>
            {data.map(r => {
              const loc = r.business_locations || {};
              const biz = r.businesses || {};
              return (
                <tr key={r.id} style={s.tr}>
                  <td style={s.td}>
                    <strong>{loc.name || biz.name || '—'}</strong>
                  </td>
                  <td style={s.td}>{loc.city || biz.city || '—'}</td>
                  <td style={{ ...s.td, color: '#888', fontSize: 12 }}>{loc.address || '—'}</td>
                  <td style={{ ...s.td, textAlign: 'center' }}>
                    {r.points_earned ? (
                      <span style={s.pill('#4CAF50')}>+{r.points_earned} xal</span>
                    ) : '—'}
                  </td>
                  <td style={{ ...s.td, color: '#555', fontSize: 12 }}>{fmtDate(r.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Tab: Xal Hərəkatı ────────────────────────────────────────────────────────
function PointsTab({ userId }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [total, setTotal]     = useState(0);

  useEffect(() => {
    (async () => {
      try {
        const { data: rows } = await db
          .from('point_logs')
          .select(`
            id, action, points, description, created_at,
            business_locations(name, city),
            businesses(name)
          `)
          .eq('user_id', userId)
          .order('created_at', { ascending: false })
          .limit(200);
        const r = rows || [];
        setData(r);
        setTotal(r.reduce((acc, x) => acc + (x.points || 0), 0));
      } catch (_) { setData([]); }
      finally { setLoading(false); }
    })();
  }, [userId]);

  if (loading) return <div style={s.center}>Yüklənir...</div>;
  if (!data.length) return <div style={s.empty}>💰 Hələ xal hərəkatı yoxdur</div>;

  const ACTION_LABELS = {
    check_in:      { label: 'QR Check-in',      icon: '📍', color: '#4CAF50' },
    redeem:        { label: 'Kupon xərcləndi',   icon: '🎟️', color: '#F44336' },
    spin:          { label: 'Spin çarxı',        icon: '🎰', color: '#9C27B0' },
    referral:      { label: 'Dost dəvəti',       icon: '👥', color: '#2196F3' },
    admin:         { label: 'Admin düzəlişi',    icon: '⚙️', color: '#FF9800' },
    reservation:   { label: 'Rezervasiya',       icon: '📅', color: '#00BCD4' },
    badge:         { label: 'Badge qazandı',     icon: '🏆', color: '#FFD700' },
  };

  return (
    <div>
      <div style={s.pointSummary}>
        <div>
          <div style={s.pointSummaryLabel}>Ümumi əməliyyat</div>
          <div style={s.pointSummaryVal}>{data.length}</div>
        </div>
        <div>
          <div style={s.pointSummaryLabel}>Toplam xal balansı (loglara görə)</div>
          <div style={{ ...s.pointSummaryVal, color: total >= 0 ? '#4CAF50' : '#F44336' }}>
            {total > 0 ? `+${total}` : total} xal
          </div>
        </div>
      </div>

      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr style={s.thead}>
              <th style={s.th}>Əməliyyat</th>
              <th style={s.th}>Kafe</th>
              <th style={s.th}>Xal</th>
              <th style={s.th}>Qeyd</th>
              <th style={s.th}>Tarix</th>
            </tr>
          </thead>
          <tbody>
            {data.map(r => {
              const info = ACTION_LABELS[r.action] || { label: r.action || '—', icon: '•', color: '#999' };
              const loc  = r.business_locations || {};
              const biz  = r.businesses || {};
              const pts  = r.points || 0;
              return (
                <tr key={r.id} style={s.tr}>
                  <td style={s.td}>
                    <span style={{ ...s.pill, backgroundColor: info.color }}>
                      {info.icon} {info.label}
                    </span>
                  </td>
                  <td style={s.td}>{loc.name || biz.name || '—'}</td>
                  <td style={{ ...s.td, textAlign: 'center', fontWeight: 700, color: pts >= 0 ? '#4CAF50' : '#F44336' }}>
                    {pts > 0 ? `+${pts}` : pts}
                  </td>
                  <td style={{ ...s.td, color: '#888', fontSize: 12 }}>{r.description || '—'}</td>
                  <td style={{ ...s.td, color: '#aaa', fontSize: 12 }}>{fmtDate(r.created_at)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Tab: Kafe Töhfəsi ────────────────────────────────────────────────────────
function ContributionTab({ userId }) {
  const [data, setData]       = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data: checkins } = await db
          .from('check_ins')
          .select('created_at, points_earned, business_locations(id, name, address, city), businesses(id, name, city)')
          .eq('user_id', userId);

        const { data: reservations } = await db
          .from('reservations')
          .select('status, party_size, business_locations(id, name, city), businesses(id, name)')
          .eq('user_id', userId);

        // Group by location/business
        const map = {};
        const getKey = (r) => {
          const loc = r.business_locations || {};
          const biz = r.businesses || {};
          return (loc.id || biz.id || 'unknown') + '||' + (loc.name || biz.name || 'Naməlum');
        };
        const getName = (r) => {
          const loc = r.business_locations || {};
          const biz = r.businesses || {};
          return loc.name || biz.name || 'Naməlum';
        };
        const getCity = (r) => {
          const loc = r.business_locations || {};
          const biz = r.businesses || {};
          return loc.city || biz.city || '';
        };

        (checkins || []).forEach(ci => {
          const k = getKey(ci);
          if (!map[k]) map[k] = { name: getName(ci), city: getCity(ci), checkins: 0, points: 0, reservations: 0, confirmedRes: 0 };
          map[k].checkins++;
          map[k].points += ci.points_earned || 0;
        });

        (reservations || []).forEach(res => {
          const k = getKey(res);
          if (!map[k]) map[k] = { name: getName(res), city: getCity(res), checkins: 0, points: 0, reservations: 0, confirmedRes: 0 };
          map[k].reservations++;
          if (res.status === 'confirmed' || res.status === 'completed') map[k].confirmedRes++;
        });

        const sorted = Object.values(map).sort((a, b) => (b.checkins + b.reservations) - (a.checkins + a.reservations));
        setData(sorted);
      } catch (_) { setData([]); }
      finally { setLoading(false); }
    })();
  }, [userId]);

  if (loading) return <div style={s.center}>Yüklənir...</div>;
  if (!data.length) return <div style={s.empty}>🏪 Hələ kafe töhfəsi yoxdur</div>;

  const totalCheckins = data.reduce((a, d) => a + d.checkins, 0);
  const totalRes      = data.reduce((a, d) => a + d.reservations, 0);
  const totalPoints   = data.reduce((a, d) => a + d.points, 0);

  return (
    <div>
      <div style={s.contributionSummary}>
        <div style={s.contribCard}>
          <div style={s.contribIcon}>📍</div>
          <div style={s.contribVal}>{totalCheckins}</div>
          <div style={s.contribLabel}>Ümumi Check-in</div>
        </div>
        <div style={s.contribCard}>
          <div style={s.contribIcon}>📅</div>
          <div style={s.contribVal}>{totalRes}</div>
          <div style={s.contribLabel}>Ümumi Rezervasiya</div>
        </div>
        <div style={s.contribCard}>
          <div style={s.contribIcon}>🏪</div>
          <div style={s.contribVal}>{data.length}</div>
          <div style={s.contribLabel}>Unikal Kafe</div>
        </div>
        <div style={s.contribCard}>
          <div style={s.contribIcon}>⭐</div>
          <div style={s.contribVal}>{totalPoints}</div>
          <div style={s.contribLabel}>Toplam Xal</div>
        </div>
      </div>

      <div style={s.tableWrap}>
        <table style={s.table}>
          <thead>
            <tr style={s.thead}>
              <th style={s.th}>#</th>
              <th style={s.th}>Kafe / Restoran</th>
              <th style={s.th}>Şəhər</th>
              <th style={s.th}>Check-in</th>
              <th style={s.th}>Rezervasiya</th>
              <th style={s.th}>Təsdiqlənmiş Res.</th>
              <th style={s.th}>Xal Qazandı</th>
            </tr>
          </thead>
          <tbody>
            {data.map((d, i) => (
              <tr key={i} style={s.tr}>
                <td style={{ ...s.td, color: '#aaa', width: 36, textAlign: 'center' }}>
                  {i === 0 ? '🥇' : i === 1 ? '🥈' : i === 2 ? '🥉' : i + 1}
                </td>
                <td style={s.td}><strong>{d.name}</strong></td>
                <td style={s.td}>{d.city || '—'}</td>
                <td style={{ ...s.td, textAlign: 'center' }}>
                  <span style={s.pill('#2196F3')}>{d.checkins}</span>
                </td>
                <td style={{ ...s.td, textAlign: 'center' }}>{d.reservations || '—'}</td>
                <td style={{ ...s.td, textAlign: 'center' }}>
                  {d.confirmedRes > 0 ? <span style={s.pill('#4CAF50')}>{d.confirmedRes}</span> : '—'}
                </td>
                <td style={{ ...s.td, textAlign: 'center', fontWeight: 700, color: '#FF6B35' }}>
                  {d.points > 0 ? `+${d.points}` : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── Tab: Korporativ ──────────────────────────────────────────────────────────
function CorporateTab({ user }) {
  const { getAdminToken } = useAuth();
  const [balance,     setBalance]     = useState(null);
  const [txns,        setTxns]        = useState(null);
  const [discounts,   setDiscounts]   = useState(null);
  const [partnerName, setPartnerName] = useState(null);
  const [loading,     setLoading]     = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const [
          { data: auditData, error: auditErr },
          { data: discRows },
        ] = await Promise.all([
          supabase.rpc('admin_get_user_corporate_audit', { p_admin_token: getAdminToken(), p_user_id: user.id }),

          db.from('business_locations')
            .select('name, address, city, corporate_discount_percent')
            .eq('is_corporate_friendly', true)
            .gt('corporate_discount_percent', 0)
            .order('corporate_discount_percent', { ascending: false }),
        ]);

        if (auditErr) console.error('Audit error:', auditErr);
        if (!auditData?.success) console.error('Audit error:', auditData?.error);

        const balRows = auditData?.balances || [];
        const txRows = auditData?.transactions || [];

        setBalance(balRows);
        setTxns(txRows);
        setDiscounts(discRows || []);
        setPartnerName(auditData?.partner_name || balRows?.[0]?.company_name || null);
      } catch (e) {
        console.error('Corporate tab error:', e);
        setBalance([]); setTxns([]); setDiscounts([]);
      } finally {
        setLoading(false);
      }
    })();
  }, [user.id]);

  if (loading) return <div style={s.center}>Yüklənir...</div>;

  const latestBal = balance[0] || null;
  const totalSpent = txns.reduce((acc, t) => acc + (parseFloat(t.amount) || 0), 0);

  return (
    <div>
      {/* Balans kartları */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px,1fr))', gap: 16, marginBottom: 28 }}>
        <div style={s.corpCard('#EDE9FE','#8B5CF6')}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>💳</div>
          <div style={s.corpCardLabel}>Cari Balans</div>
          <div style={{ ...s.corpCardVal, color: latestBal ? (parseFloat(latestBal.balance) > 0 ? '#8B5CF6' : '#DC2626') : '#aaa' }}>
            {latestBal ? `${parseFloat(latestBal.balance).toFixed(2)} ₼` : '—'}
          </div>
          {latestBal && (
            <div style={{ fontSize: 11, color: '#8B5CF6', marginTop: 4 }}>
              {latestBal.period_month}/{latestBal.period_year} · Limit: {parseFloat(latestBal.monthly_limit).toFixed(2)} ₼
            </div>
          )}
        </div>

        <div style={s.corpCard('#FEF3C7','#D97706')}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>💸</div>
          <div style={s.corpCardLabel}>Ümumi Xərclənən</div>
          <div style={{ ...s.corpCardVal, color: '#D97706' }}>{totalSpent.toFixed(2)} ₼</div>
          <div style={{ fontSize: 11, color: '#D97706', marginTop: 4 }}>{txns.length} əməliyyat</div>
        </div>

        <div style={s.corpCard('#D1FAE5','#059669')}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>🏢</div>
          <div style={s.corpCardLabel}>Şirkət</div>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#059669', wordBreak: 'break-word' }}>
            {partnerName || (user.corporate_partner_id ? '…' : 'Qoşulmayıb')}
          </div>
        </div>

        <div style={s.corpCard('#FEE2E2','#DC2626')}>
          <div style={{ fontSize: 28, marginBottom: 6 }}>🎁</div>
          <div style={s.corpCardLabel}>Endirimli Kafelər</div>
          <div style={{ ...s.corpCardVal, color: '#DC2626' }}>{discounts.length}</div>
          <div style={{ fontSize: 11, color: '#DC2626', marginTop: 4 }}>istifadə edə bilər</div>
        </div>
      </div>

      {/* Xərcləmə tarixçəsi */}
      <div style={s.section}>
        <h3 style={s.sectionTitle}>💸 Xərcləmə Tarixçəsi</h3>
        {txns.length === 0 ? (
          <div style={{ color: '#aaa', fontSize: 13, textAlign: 'center', padding: 20 }}>Hələ xərcləmə yoxdur</div>
        ) : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr style={s.thead}>
                  <th style={s.th}>Kafe</th>
                  <th style={s.th}>Şəhər</th>
                  <th style={s.th}>Məbləğ</th>
                  <th style={s.th}>Öncə → Sonra</th>
                  <th style={s.th}>Tarix</th>
                </tr>
              </thead>
              <tbody>
                {txns.map((t, i) => {
                  const loc = { name: t.name, address: t.address, city: t.city };
                  return (
                    <tr key={i} style={s.tr}>
                      <td style={s.td}><strong>{loc.name || '—'}</strong>
                        {loc.address && <div style={s.sub}>{loc.address}</div>}
                      </td>
                      <td style={s.td}>{loc.city || '—'}</td>
                      <td style={s.td}>
                        <span style={{ fontWeight: 700, color: '#DC2626' }}>
                          -{parseFloat(t.amount).toFixed(2)} ₼
                        </span>
                      </td>
                      <td style={{ ...s.td, fontSize: 12 }}>
                        {t.balance_before != null
                          ? <span>
                              <span style={{ color: '#888' }}>{parseFloat(t.balance_before).toFixed(2)} ₼</span>
                              <span style={{ color: '#bbb', margin: '0 4px' }}>→</span>
                              <span style={{ color: t.balance_after >= 0 ? '#059669' : '#DC2626', fontWeight: 600 }}>
                                {t.balance_after != null ? `${parseFloat(t.balance_after).toFixed(2)} ₼` : '—'}
                              </span>
                            </span>
                          : '—'}
                      </td>
                      <td style={{ ...s.td, color: '#aaa', fontSize: 12 }}>{fmtDate(t.created_at)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Endirimli kafelər */}
      <div style={s.section}>
        <h3 style={s.sectionTitle}>🎁 İstifadə Edə Biləcəyi Endirimli Kafelər</h3>
        {discounts.length === 0 ? (
          <div style={{ color: '#aaa', fontSize: 13, textAlign: 'center', padding: 20 }}>Hal-hazırda endirimli kafe yoxdur</div>
        ) : (
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr style={s.thead}>
                  <th style={s.th}>Kafe</th>
                  <th style={s.th}>Şəhər</th>
                  <th style={s.th}>Ünvan</th>
                  <th style={s.th}>Endirim %</th>
                </tr>
              </thead>
              <tbody>
                {discounts.map((d, i) => (
                  <tr key={i} style={s.tr}>
                    <td style={s.td}><strong>{d.name || '—'}</strong></td>
                    <td style={s.td}>{d.city || '—'}</td>
                    <td style={{ ...s.td, color: '#888', fontSize: 12 }}>{d.address || '—'}</td>
                    <td style={s.td}>
                      <span style={{ ...s.pill('#059669'), fontSize: 13 }}>
                        %{parseFloat(d.corporate_discount_percent).toFixed(0)} endirim
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Aylıq balans tarixi */}
      {balance.length > 1 && (
        <div style={s.section}>
          <h3 style={s.sectionTitle}>📅 Aylıq Balans Tarixi</h3>
          <div style={s.tableWrap}>
            <table style={s.table}>
              <thead>
                <tr style={s.thead}>
                  <th style={s.th}>Ay / İl</th>
                  <th style={s.th}>Qalıq Balans</th>
                  <th style={s.th}>Aylıq Limit</th>
                  <th style={s.th}>İstifadə %</th>
                </tr>
              </thead>
              <tbody>
                {balance.map((b, i) => {
                  const used = parseFloat(b.monthly_limit) - parseFloat(b.balance);
                  const pct  = b.monthly_limit > 0 ? Math.round((used / parseFloat(b.monthly_limit)) * 100) : 0;
                  return (
                    <tr key={i} style={s.tr}>
                      <td style={s.td}>{b.period_month}/{b.period_year}</td>
                      <td style={{ ...s.td, fontWeight: 700, color: parseFloat(b.balance) > 0 ? '#059669' : '#DC2626' }}>
                        {parseFloat(b.balance).toFixed(2)} ₼
                      </td>
                      <td style={s.td}>{parseFloat(b.monthly_limit).toFixed(2)} ₼</td>
                      <td style={s.td}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <div style={{ flex: 1, height: 6, backgroundColor: '#F0F0F0', borderRadius: 3, overflow: 'hidden' }}>
                            <div style={{ width: `${Math.min(pct,100)}%`, height: '100%', backgroundColor: pct >= 90 ? '#DC2626' : pct >= 60 ? '#F59E0B' : '#059669', borderRadius: 3 }} />
                          </div>
                          <span style={{ fontSize: 12, fontWeight: 600, color: '#555', minWidth: 32 }}>{pct}%</span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Ana Modal ────────────────────────────────────────────────────────────────
export default function UserAuditModal({ user, onClose }) {
  const isCorporateUser = user?.user_type === 'corporate' || user?.is_corporate === true;

  const TABS_DYNAMIC = [
    { id: 'overview',      label: '📊 Ümumi',         icon: FaChartBar },
    { id: 'reservations',  label: '📅 Rezervasiyalar', icon: FaCalendarAlt },
    { id: 'checkins',      label: '📍 QR Tarixçəsi',   icon: FaQrcode },
    { id: 'points',        label: '💰 Xal Hərəkatı',   icon: FaCoins },
    { id: 'contribution',  label: '🏪 Kafe Töhfəsi',   icon: FaStore },
    ...(isCorporateUser ? [{ id: 'corporate', label: '💼 Korporativ', icon: FaBuilding }] : []),
  ];

  const [activeTab, setActiveTab] = useState('overview');
  const [stats, setStats]         = useState(null);
  const [topBusinesses, setTopBiz]= useState([]);
  const [loadingStats, setLoadingStats] = useState(true);

  useEffect(() => {
    if (!user) return;
    (async () => {
      try {
        const [
          { count: totalCheckins },
          { data: checkinData },
          { count: friendCount },
          { count: meetupCount },
          { count: spinCount },
          { count: reservationCount },
        ] = await Promise.all([
          db.from('check_ins').select('*', { count: 'exact', head: true }).eq('user_id', user.id),
          db.from('check_ins').select('business_id, businesses(name)').eq('user_id', user.id),
          db.from('friendships').select('*', { count: 'exact', head: true })
            .or(`user_id.eq.${user.id},friend_id.eq.${user.id}`).eq('status', 'accepted'),
          db.from('meetups').select('*', { count: 'exact', head: true }).eq('creator_id', user.id),
          db.from('daily_spins').select('*', { count: 'exact', head: true }).eq('user_id', user.id),
          db.from('reservations').select('*', { count: 'exact', head: true }).eq('user_id', user.id),
        ]);

        const uniqueBusinesses = new Set((checkinData || []).map(c => c.business_id)).size;
        const accountAge = Math.floor((new Date() - new Date(user.created_at)) / 86400000);

        setStats({ totalCheckins, uniqueBusinesses, friendCount, meetupCount, spinCount,
          reservationCount, points: user.points || 0, accountAge });

        // Top 5 businesses
        const bizMap = {};
        (checkinData || []).forEach(c => {
          const id = c.business_id;
          if (!id) return;
          if (!bizMap[id]) bizMap[id] = { name: c.businesses?.name || 'Naməlum', count: 0 };
          bizMap[id].count++;
        });
        setTopBiz(Object.values(bizMap).sort((a, b) => b.count - a.count).slice(0, 5));
      } catch (_) {}
      finally { setLoadingStats(false); }
    })();
  }, [user]);

  if (!user) return null;

  return (
    <>
      <div style={s.overlay} onClick={onClose} />
      <div style={s.modal}>
        {/* Header */}
        <div style={s.header}>
          <div style={s.headerLeft}>
            <img
              src={user.avatar_url || `https://ui-avatars.com/api/?name=${encodeURIComponent(user.full_name || 'U')}&background=FF6B35&color=fff&size=80`}
              alt={user.full_name}
              style={s.avatar}
            />
            <div>
              <h2 style={s.name}>{user.full_name || '—'}</h2>
              {user.username && <p style={s.username}>@{user.username}</p>}
              <p style={s.email}>{user.email}</p>
              <div style={{ display: 'flex', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
                <span style={{
                  ...s.metaBadge,
                  ...(isCorporateUser ? { backgroundColor: '#EDE9FE', color: '#7C3AED' } : {}),
                }}>
                  {isCorporateUser ? '🏢 Korporativ' : user.user_type === 'tourist' ? '✈️ Turist' : '🏠 Local'}
                </span>
                <span style={s.metaBadge}>📆 {fmtDateOnly(user.created_at)}</span>
                {user.points > 0 && <span style={{ ...s.metaBadge, backgroundColor: '#FFF3E0', color: '#E65100' }}>⭐ {user.points} xal</span>}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={s.closeBtn}><FaTimes size={20} /></button>
        </div>

        {/* Tabs */}
        <div style={s.tabBar}>
          {TABS_DYNAMIC.map(t => (
            <button
              key={t.id}
              style={{
                ...s.tab,
                ...(activeTab === t.id ? (t.id === 'corporate' ? s.tabActiveCorp : s.tabActive) : {}),
              }}
              onClick={() => setActiveTab(t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Content */}
        <div style={s.content}>
          {activeTab === 'overview'     && (loadingStats
            ? <div style={s.center}>Yüklənir...</div>
            : <OverviewTab user={user} stats={stats} topBusinesses={topBusinesses} />
          )}
          {activeTab === 'reservations' && <ReservationsTab userId={user.id} />}
          {activeTab === 'checkins'     && <CheckinsTab userId={user.id} />}
          {activeTab === 'points'       && <PointsTab userId={user.id} />}
          {activeTab === 'contribution' && <ContributionTab userId={user.id} />}
          {activeTab === 'corporate'    && <CorporateTab user={user} />}
        </div>
      </div>
    </>
  );
}

// ─── Styles ───────────────────────────────────────────────────────────────────
const s = {
  overlay: { position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.65)', zIndex: 2000 },
  modal: {
    position: 'fixed', top: '50%', left: '50%', transform: 'translate(-50%,-50%)',
    backgroundColor: '#fff', borderRadius: 18, width: '97%', maxWidth: 1100,
    maxHeight: '92vh', overflowY: 'auto', zIndex: 2001,
    boxShadow: '0 24px 64px rgba(0,0,0,0.25)',
  },
  header: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start',
    padding: '24px 28px', borderBottom: '1px solid #F0F0F0',
    position: 'sticky', top: 0, backgroundColor: '#fff', zIndex: 10,
  },
  headerLeft: { display: 'flex', gap: 18, alignItems: 'flex-start' },
  avatar: { width: 72, height: 72, borderRadius: '50%', objectFit: 'cover', border: '3px solid #FF6B35', flexShrink: 0 },
  name: { fontSize: 22, fontWeight: 800, color: '#1A1A2E', marginBottom: 2 },
  username: { fontSize: 14, color: '#888', marginBottom: 2 },
  email: { fontSize: 13, color: '#aaa' },
  metaBadge: { padding: '3px 10px', backgroundColor: '#F5F5F5', borderRadius: 20, fontSize: 12, fontWeight: 600, color: '#555' },
  closeBtn: {
    backgroundColor: '#F44336', color: '#fff', border: 'none', borderRadius: '50%',
    width: 38, height: 38, display: 'flex', alignItems: 'center', justifyContent: 'center',
    cursor: 'pointer', flexShrink: 0,
  },
  tabBar: {
    display: 'flex', gap: 0, borderBottom: '2px solid #F0F0F0',
    overflowX: 'auto', paddingLeft: 20,
  },
  tab: {
    padding: '13px 18px', fontSize: 13, fontWeight: 600, color: '#888',
    backgroundColor: 'transparent', border: 'none', borderBottom: '3px solid transparent',
    marginBottom: -2, cursor: 'pointer', whiteSpace: 'nowrap', transition: 'all 0.2s',
  },
  tabActive: { color: '#FF6B35', borderBottomColor: '#FF6B35' },
  content: { padding: '24px 28px', minHeight: 300 },

  // Stats
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 16, marginBottom: 24 },
  statCard: { display: 'flex', alignItems: 'center', gap: 14, padding: '16px 18px', backgroundColor: '#F8F9FA', borderRadius: 12 },
  statLabel: { fontSize: 11, color: '#999', marginBottom: 4 },
  statValue: { fontSize: 26, fontWeight: 800 },

  section: { backgroundColor: '#F8F9FA', borderRadius: 12, padding: '20px 22px', marginBottom: 20 },
  sectionTitle: { fontSize: 16, fontWeight: 700, color: '#1A1A2E', marginBottom: 16 },

  badge: (bg, color = '#fff') => ({
    padding: '6px 14px', backgroundColor: bg, color, borderRadius: 20,
    fontSize: 13, fontWeight: 600,
  }),

  // Table
  tableWrap: { overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  thead: { backgroundColor: '#F8F9FA' },
  th: { padding: '10px 14px', textAlign: 'left', fontWeight: 700, color: '#555', fontSize: 12, whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #F5F5F5' },
  td: { padding: '11px 14px', color: '#333', verticalAlign: 'middle' },
  sub: { fontSize: 11, color: '#aaa', marginTop: 2 },
  pill: (bg = '#FF6B35') => ({
    display: 'inline-block', padding: '3px 10px',
    backgroundColor: bg, color: '#fff',
    borderRadius: 12, fontSize: 12, fontWeight: 600,
  }),

  // Point summary
  pointSummary: {
    display: 'flex', gap: 20, marginBottom: 20, padding: '16px 20px',
    backgroundColor: '#F8F9FA', borderRadius: 12, flexWrap: 'wrap',
  },
  pointSummaryLabel: { fontSize: 12, color: '#999', marginBottom: 4 },
  pointSummaryVal: { fontSize: 22, fontWeight: 800, color: '#1A1A2E' },

  // Contribution summary
  contributionSummary: { display: 'flex', gap: 16, marginBottom: 24, flexWrap: 'wrap' },
  contribCard: {
    flex: 1, minWidth: 120, padding: '16px 20px', backgroundColor: '#F8F9FA',
    borderRadius: 12, textAlign: 'center',
  },
  contribIcon: { fontSize: 28, marginBottom: 6 },
  contribVal:  { fontSize: 28, fontWeight: 800, color: '#1A1A2E', marginBottom: 4 },
  contribLabel:{ fontSize: 11, color: '#999', fontWeight: 600 },

  // Corporate
  tabActiveCorp: { color: '#8B5CF6', borderBottomColor: '#8B5CF6' },
  corpCard: (bg, color) => ({
    padding: '18px 20px', backgroundColor: bg, borderRadius: 14, textAlign: 'center',
  }),
  corpCardLabel: { fontSize: 11, color: '#666', fontWeight: 600, marginBottom: 6 },
  corpCardVal:   { fontSize: 26, fontWeight: 800, color: '#1A1A2E' },

  // Misc
  countNote: { fontSize: 13, color: '#888', marginBottom: 14 },
  center: { display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 60, color: '#aaa' },
  empty:  { textAlign: 'center', padding: 60, fontSize: 15, color: '#aaa' },
};
